import { type AnyFormApi, useForm } from '@tanstack/react-form';
import { t } from 'i18next';
import { createContext, useContext } from 'react';
import { useApplyDraftFormValues } from '../../../utils/form';
import { CUSTOMER_GROUP_BASE_SLUG, INITIAL_CUSTOMER_GROUP } from '../constants';
import {
  type CustomerGroupBuilderSettingsState,
  type CustomerGroupBuilderState,
  type CustomerGroupBuilderValidated,
  customerGroupBuilderSchema,
} from '../types/customer-group.schema';

type CustomerGroupBuilderForm = {
  initialState?: CustomerGroupBuilderState;
  draftState?: CustomerGroupBuilderState;
  onSubmit: (payload: CustomerGroupBuilderValidated) => void;
  onValidationError?: ({ formApi }: { formApi: AnyFormApi }) => void;
};

export function useCustomerGroupBuilderFormLogic({
  initialState,
  draftState,
  onSubmit,
  onValidationError,
}: CustomerGroupBuilderForm) {
  const form = useForm({
    defaultValues: initialState ?? INITIAL_CUSTOMER_GROUP,
    validators: {
      onSubmit: customerGroupBuilderSchema.superRefine((customerGroup, ctx) => {
        if (
          initialState?.slug !== CUSTOMER_GROUP_BASE_SLUG &&
          customerGroup.slug === CUSTOMER_GROUP_BASE_SLUG
        ) {
          ctx.addIssue({
            code: 'custom',
            message: t('errors:forms.duplicate_handle'),
            path: ['slug'],
          });
        }
      }),
    },
    onSubmit: async ({ value }) => {
      // Extract allowDefaultBillingModification from allowAddressEditing as they were stored within
      // a single setting to be displayed in a single card
      const transformedSettings: CustomerGroupBuilderSettingsState = {
        ...value.settings,
        allowDefaultBillingModification:
          value.settings.allowAddressEditing?.allowDefaultBillingModification,
      };

      const transformedValue: CustomerGroupBuilderState = {
        ...value,
        settings: transformedSettings,
      };

      const payload = customerGroupBuilderSchema.parse(transformedValue);

      await onSubmit(payload);
    },
    onSubmitInvalid: onValidationError,
  });

  useApplyDraftFormValues(form, draftState);

  return form;
}

type CustomerGroupBuilderFormContextType = ReturnType<typeof useCustomerGroupBuilderFormLogic>;

export const CustomerGroupBuilderContext =
  createContext<CustomerGroupBuilderFormContextType | null>(null);

export function useCustomerGroupBuilderForm() {
  const context = useContext(CustomerGroupBuilderContext);
  if (!context) {
    throw new Error(
      'useCustomerGroupsBuilderContext must be used within a CustomerGroupBuilderContext.Provider ',
    );
  }
  return context;
}
